import React, { useState } from 'react';
import {
  Card,
  Button,
  Typography,
  Spin,
  Alert,
  Avatar,
  Popover,
  Descriptions,
  Modal,
  Tag,
  Radio,
  Space
} from 'antd';
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  BookOutlined,
  UserOutlined,
  LinkedinOutlined,
  StarFilled,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import useCourseDetails from 'PlatForms/SelfLMS/Hooks/Courses/useCourseDetail';
import useAvailableCourses from 'PlatForms/SelfLMS/Hooks/Courses/useCourses';
import { 
  ExpertRecommendationLevelEnum, 
  ExpertRecommendationLevelEnumLabels 
} from 'Common/Constants/Enums/ExpertRecommendation';

import { toast } from 'react-toastify';
import { selfLearnerService } from 'PlatForms/SelfLMS/Services/Courses/CourseService';


const { Title, Text, Paragraph } = Typography;

const CourseDetail = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { course, loading, error } = useCourseDetails(courseId);
  const { enrollInCourse } = useAvailableCourses();
  const [isExpertSupportModalVisible, setIsExpertSupportModalVisible] = useState(false);
const [includeExpertSupport, setIncludeExpertSupport] = useState(false);



  const [enrolling, setEnrolling] = useState(false);




  // Price Modal State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPriceData, setSelectedPriceData] = useState(null);

  const formatMinutesToHours = (minutes) => {
  if (!minutes || minutes <= 0) return "Not specified";

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h${remainingMinutes}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${remainingMinutes}m`;
};

const showExpertSupportModal = (course) => {
  const isRequired =
    course?.courseExpertSupport?.recommendationLevel ===
    ExpertRecommendationLevelEnum.Required;

  setIncludeExpertSupport(isRequired ? true : false);
  setIsExpertSupportModalVisible(true);
};

const handleEnrollOrBuy = async () => {
  try {
    setEnrolling(true);

    // FREE COURSE
    if (!course.isPaid) {
      await enrollInCourse(course.id);
      toast.success(`Successfully enrolled in "${course.title}"`);
      navigate('/selfLearner-dashboard/enrolled-courses');
      return;
    }

    // PAID COURSE FLOW - Check for expert support
    if (course.hasActiveExpertSupport) {
      // First, fetch full course details to get expert support info
      try {
        const fullCourseDetails = await selfLearnerService.getCourseDetails(course.id);
        
        if (fullCourseDetails?.courseExpertSupport) {
          // Merge the expert support data with the course
          const courseWithExpertSupport = {
            ...course,
            courseExpertSupport: fullCourseDetails.courseExpertSupport
          };
          showExpertSupportModal(courseWithExpertSupport);
        } else {
          proceedWithPurchase(course.id, false);
        }
      } catch (error) {
        console.error("Error fetching course details:", error);
        proceedWithPurchase(course.id, false);
      }
      return;
    }

    // REGULAR PAID COURSE WITHOUT EXPERT SUPPORT
    proceedWithPurchase(course.id, false);
    
  } catch (error) {
    console.error("Enroll / Buy failed:", error);
    toast.error(
      error.response?.data?.details ||
      "Something went wrong. Please try again."
    );
  } finally {
    setEnrolling(false);
  }
};

const proceedWithPurchase = async (courseId, includeSupport) => {
  try {
    const addOnExpertSupport = includeSupport;
    const purchaseResponse = await selfLearnerService.requestCoursePurchase(
      courseId, 
      addOnExpertSupport
    );

    if (purchaseResponse?.sessionUrl) {
      window.location.href = purchaseResponse.sessionUrl;
    } else {
      toast.error("Unable to initiate payment. Please try again.");
    }
  } catch (error) {
    console.error("Purchase failed:", error);
    toast.error(error.response?.data?.message || "Payment failed");
  }
};



  // Logic to convert days to readable Y/M/D
  const formatDuration = (totalDays) => {
    if (!totalDays) return "Not specified";
    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const days = (totalDays % 365) % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    return parts.join(", ");
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";

    const [hours, minutes] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);

    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt}m`;
    if (minutesInt === 0) return `${hoursInt}h`;

    return `${hoursInt}h ${minutesInt}m`;
  };

  const openPriceModal = (e, course) => {
    e.stopPropagation(); // Prevents navigating to course details
    setSelectedPriceData(course);
    setIsModalVisible(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Helper function to get author name
  const getAuthorName = (course) => {
    if (course.courseAuthor && course.courseAuthor.name) {
      return course.courseAuthor.name;
    }
    if (course.authorName) {
      return course.authorName;
    }
    return "Unknown Author";
  };


  const getLinkedInUrl = (course) => {
    if (course.courseAuthor && course.courseAuthor.linkedinUrl) {
      return course.courseAuthor.linkedinUrl;
    }
    if (course.linkedinUrl) {
      return course.linkedinUrl;
    }
    if (course.authorLinkedIn) {
      return course.authorLinkedIn;
    }
    return null;
  };



  const handleBack = () => {
    navigate('/selfLearner-dashboard/available-courses');
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Course Details"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!course) {
    return (
      <Alert
        message="Course Not Found"
        description="The requested course could not be found."
        type="warning"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  const linkedInUrl = getLinkedInUrl(course);
  const authorName = getAuthorName(course);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f7fafc',
    }}>

      {/* Vertical Stack Container */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '16px'
      }}>

        {/* Header with Back Button on Right */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{ textAlign: 'left' }}>
    <Title level={2} style={{ marginBottom: '12px', color: '#1C1917', fontSize: '20px' }}>
  {course.title}
  
  {/* Expert Support Indicator */}
  {course.hasActiveExpertSupport && (
    <span style={{ 
      marginLeft: '8px', 
      fontSize: '11px', 
      color: '#faad14', 
      display: 'inline-flex', 
      alignItems: 'center',
      verticalAlign: 'middle',
      fontWeight: 'normal',
      background: '#fffbe6',
      padding: '2px 6px',
      borderRadius: '4px',
      border: '1px solid #ffe58f'
    }}>
      <StarFilled style={{ marginRight: '4px' }} />
      Expert Support
    </span>
  )}
</Title>
            <Text type="secondary" style={{ fontSize: '14px' }}> {/* Smaller font */}
              {course.title}
            </Text>
          </div>

          {/* Back Button - Right Side */}
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            size="small"
          >
            Back to Courses 
          </Button>
        </div>

        {/* Course Header Card */}
        <Card
          style={{
            width: '100%',
            marginBottom: '16px',
            border: '2px solid #E7E1D5ff',
            borderTop: 'none',
            borderRadius: '0px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            position: 'relative', // Required for absolute positioning
            overflow: 'hidden',   // Required to clip the banner corners
          }}
          bodyStyle={{ padding: '20px' }}
        >
          {/* CLICKABLE PRICE/FREE BANNER */}
          <div
            onClick={(e) => openPriceModal(e, course)}
            style={{
              position: "absolute",
              // These coordinates and width are balanced for the detail card
              top: 20,
              right: -40,
              background: course.isPaid ? "#9A6B0E" : "#52c41a",
              color: "white",
              padding: "6px 0", // Vertical padding for thickness
              transform: "rotate(45deg)",
              fontSize: "16px",
              fontWeight: "bold",
              zIndex: 10,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              textAlign: "center",
              width: "150px",   // Increased width to ensure it covers the corner
              cursor: "pointer",
              lineHeight: "1"
            }}
          >
            {course.isPaid ? (
              `${course.pricing?.currency === "USD" ? "$" : (course.pricing?.currency || "$")}${course.pricing?.price || 0}`
            ) : (
              "FREE"
            )}
          </div>

          <Title level={2} style={{ marginBottom: '12px', color: '#1C1917', fontSize: '20px' }}> {/* Smaller title */}
            {course.title}
          </Title>

          {/* Author Information with LinkedIn */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Avatar size="small" icon={<UserOutlined />} />
            {authorName ? (
              <Popover
                content={
                  <div style={{ padding: '4px' }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Text style={{ fontSize: "16px" }}>
                        {authorName}
                      </Text>
                      {linkedInUrl ? (
                        <a
                          href={linkedInUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            backgroundColor: '#0077B5',
                            color: 'white',
                            textDecoration: 'none',
                            transition: 'all 0.3s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <LinkedinOutlined style={{ fontSize: '14px' }} />
                        </a>
                      ) : (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          (No LinkedIn)
                        </Text>
                      )}
                    </div>
                  </div>
                }
                title="Author Information"
                trigger="click"
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize: "13px",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textDecorationStyle: "dotted",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {authorName}
                </Text>
              </Popover>
            ) : (
              <Text type="secondary" style={{ fontSize: "13px" }}>
                No author available
              </Text>
            )}
          </div>

          <Paragraph style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '16px', color: '#57534E' }}> {/* Smaller text */}
            {course.description}
          </Paragraph>

          {/* Course Metadata */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0', // Reduced padding
            borderTop: '1px solid #E7E1D5',
            borderBottom: '1px solid #E7E1D5',
            marginBottom: '16px' // Reduced margin
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}> {/* Reduced gap */}
              <ClockCircleOutlined style={{ color: "#666", fontSize: "12px" }} /> {/* Smaller icon */}
              <Text type="secondary" style={{ fontSize: "12px" }}> {/* Smaller text */}
                {formatTime(course.estimatedTime)}
              </Text>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}> {/* Reduced gap */}
              <BookOutlined style={{ color: "#666", fontSize: "12px" }} /> {/* Smaller icon */}
              <Text type="secondary" style={{ fontSize: "12px" }}> {/* Smaller text */}
                {course.modules?.length || 0} Modules
              </Text>
            </div>

            {course.enrolledCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}> {/* Reduced gap */}
                <UserOutlined style={{ color: "#666", fontSize: "12px" }} /> {/* Smaller icon */}
                <Text type="secondary" style={{ fontSize: "12px" }}> {/* Smaller text */}
                  {course.enrolledCount} Enrolled
                </Text>
              </div>
            )}
          </div>

                    {/* Expert Support Information Section - Integrated in Header Card */}
          {course.hasActiveExpertSupport && course.courseExpertSupport && (
            <div style={{
              padding: '12px',
              backgroundColor: '#FBF6EC',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #F6EFDD'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <TeamOutlined style={{ color: '#9A6B0E', marginRight: '8px', fontSize: '16px' }} />
                <Text strong style={{ color: '#9A6B0E' }}>
                  Expert Support Available
                </Text>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Avatar 
                  src={course.courseExpertSupport.expert?.profileImage}
                  icon={<UserOutlined />}
                  size="small"
                  style={{ marginRight: '8px' }}
                />
                <div>
                  <Text strong style={{ display: 'block', fontSize: '13px' }}>
                    {course.courseExpertSupport.expert?.firstName} {course.courseExpertSupport.expert?.lastName}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {course.courseExpertSupport.expert?.yearsOfExperience} years experience
                  </Text>
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong style={{ color: '#9A6B0E', fontSize: '13px' }}>
                    ${course.courseExpertSupport.unitPrice}/{formatMinutesToHours(course.courseExpertSupport.unitDurationMinutes)}
                  </Text>
                  <Text type="secondary" style={{ marginLeft: '8px', fontSize: '11px' }}>
                    ({course.courseExpertSupport.allowedUnits} sessions)
                  </Text>
                </div>
                
                <Tag 
                  color={
                    course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.Required ? 'red' :
                    course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.StronglyRecommended ? 'orange' :
                    course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.Recommended ? 'blue' : 'default'
                  }
                  style={{ fontSize: '10px' }}
                >
                  {ExpertRecommendationLevelEnumLabels[course.courseExpertSupport.recommendationLevel]}
                </Tag>
              </div>
            </div>
          )}

<Button
  type="primary"
  block
  size="large"
  loading={enrolling}
  onClick={handleEnrollOrBuy}
  style={{ marginTop: '16px' }}
>
  {course.isPaid ? "Buy Now" : "Enroll Now"}
</Button>


          {/* Progress Section */}
          {course.enrollment && (
            <div style={{ marginBottom: '12px' }}> {/* Reduced margin */}

              <div style={{ marginTop: '4px' }}> {/* Reduced margin */}
                <Text type="secondary" style={{ fontSize: '11px' }}> {/* Smaller text */}
                  Enrolled on: {formatDate(course.enrollment.createdOn)}
                </Text>
              </div>
            </div>
          )}
        </Card>

 

        {/* Modules Section */}
        <div style={{ marginBottom: '16px' }}> {/* Reduced margin */}
          <Title level={3} style={{ marginBottom: '16px', color: '#1C1917', fontSize: '18px' }}> {/* Smaller title */}
            Course Modules
          </Title>

          {course.modules && course.modules.length > 0 ? (
            course.modules.map((module, index) => (
              <Card
                key={module.id}
                style={{
                  width: '100%',
                  marginBottom: '12px',
                  border: '2px solid #E7E1D5ff',
                  borderTop: 'none',
                  borderRadius: '0px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer'
                }}
                hoverable
                bodyStyle={{ padding: '16px' }}
              >
                <Title level={4} style={{ margin: 0, marginBottom: '8px', lineHeight: "1.3", fontSize: '16px' }}> {/* Smaller title */}
                  {index + 1}. {module.title}
                </Title>

                {/* Module Description */}
                {module.description && (
                  <Paragraph style={{ margin: 0, marginBottom: '8px', fontSize: '13px' }}> {/* Smaller text */}
                    {module.description}
                  </Paragraph>
                )}

                {/* Module Metadata */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <ClockCircleOutlined style={{ color: "#666", fontSize: "12px" }} />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {formatTime(module.estimatedTime)}
                    </Text>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <BookOutlined style={{ color: "#666", fontSize: "12px" }} />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {module.pages?.length || 0} Pages
                    </Text>
                  </div>
                </div>


              </Card>
            ))
          ) : (
            <Card
              style={{
                width: '100%',
                border: '2px solid #E7E1D5ff',
                borderTop: 'none',
                borderRadius: '0px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
              }}
              bodyStyle={{ padding: '20px', textAlign: 'center' }}
            >
              <BookOutlined style={{ fontSize: '36px', color: '#d9d9d9', marginBottom: '12px' }} /> {/* Smaller icon */}
              <Title level={4} style={{ color: '#666', marginBottom: '6px', fontSize: '16px' }}> {/* Smaller title */}
                No Modules Available
              </Title>
              <Text style={{ color: '#666', fontSize: '13px' }}> {/* Smaller text */}
                This course does not have any modules yet.
              </Text>
            </Card>
          )}
        </div>

        {/* Prerequisites Section */}
        {course.coursePrerequisites && course.coursePrerequisites.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Title level={3} style={{ marginBottom: '16px', color: '#1C1917', fontSize: '18px' }}>
              Prerequisites
            </Title>

            {course.coursePrerequisites.map((prerequisite, index) => (
              <Card
                key={index}
                style={{
                  width: '100%',
                  marginBottom: '8px',
                  border: '2px solid #E7E1D5ff',
                  borderTop: 'none',
                  borderRadius: '0px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                }}
                bodyStyle={{ padding: '12px' }}
              >
                <Text style={{ fontSize: '13px', lineHeight: '1.4' }}>
                  {prerequisite.description}
                </Text>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* EXPERT SUPPORT MODAL */}
<Modal
  title="Expert Support Available"
  open={isExpertSupportModalVisible}
  onCancel={() => setIsExpertSupportModalVisible(false)}
  footer={[
    <Button key="cancel" onClick={() => setIsExpertSupportModalVisible(false)}>
      Cancel
    </Button>,
   <Button
  key="purchase"
  type="primary"
  onClick={() => {
    const supportRequired =
      course?.courseExpertSupport?.recommendationLevel ===
      ExpertRecommendationLevelEnum.Required;
      
    const finalIncludeSupport = supportRequired
      ? true
      : includeExpertSupport === true;

    setIsExpertSupportModalVisible(false);
    proceedWithPurchase(course.id, finalIncludeSupport);
  }}
>
      Proceed to Purchase
    </Button>
  ]}
  width={600}
>
  {course && (
    <div>
      <Title level={4}>{course.title}</Title>
      
      <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Course Price">
          <Text strong style={{ fontSize: '16px' }}>
            {course.pricing?.currency} {course.pricing?.price}
          </Text>
        </Descriptions.Item>
        
        <Descriptions.Item label="Access Duration">
          {formatDuration(course.pricing?.accessDurationDays)}
        </Descriptions.Item>
      </Descriptions>
      
      {course.courseExpertSupport && (
        <>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#FBF6EC', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <Title level={5} style={{ marginBottom: '8px' }}>
              <TeamOutlined /> Expert Support Service
            </Title>
            
            <div style={{ marginBottom: '12px' }}>
              <Avatar 
                src={course.courseExpertSupport.expert?.profileImage}
                icon={<UserOutlined />}
                size="small"
                style={{ marginRight: '8px' }}
              />
              <Text strong>
                {course.courseExpertSupport.expert?.firstName} {course.courseExpertSupport.expert?.lastName}
              </Text>
            </div>
            
            <Text type="secondary">
              {course.courseExpertSupport.description}
            </Text>
            
            <div style={{ marginTop: '8px' }}>
              <Text strong>
                ${course.courseExpertSupport.unitPrice}/
                {formatMinutesToHours(
                  course.courseExpertSupport.unitDurationMinutes
                )}
              </Text>
            </div>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Recommendation: </Text>
            <Tag color={
              course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.Required ? 'red' :
              course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.StronglyRecommended ? 'orange' :
              course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.Recommended ? 'blue' : 'default'
            }>
              {ExpertRecommendationLevelEnumLabels[course.courseExpertSupport.recommendationLevel]}
            </Tag>
          </div>
          
          {course.courseExpertSupport.recommendationLevel !== ExpertRecommendationLevelEnum.Required && (
            <div style={{ marginBottom: '16px' }}>
              <Text>Would you like to include expert support with your purchase?</Text>
            </div>
          )}
          
          <Radio.Group
            value={includeExpertSupport}
            onChange={(e) => setIncludeExpertSupport(Boolean(e.target.value))}
          >
            <Space direction="vertical">
              <Radio 
                value={false} 
                disabled={course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.Required}
              >
                <div>
                  <Text>Course Only</Text>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {course.pricing?.currency} {course.pricing?.price}
                  </div>
                </div>
              </Radio>
              <Radio value={true}>
                <div>
                  <Text>Course + Expert Support</Text>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {course.pricing?.currency} {course.pricing?.price} + 
                    ${course.courseExpertSupport.unitPrice} × 
                    {course.courseExpertSupport.allowedUnits} units = 
                    <Text strong> {course.pricing?.currency} {
                      Number(course.pricing?.price) + 
                      (Number(course.courseExpertSupport.unitPrice) * 
                       Number(course.courseExpertSupport.allowedUnits))
                    }</Text>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Includes {course.courseExpertSupport.allowedUnits} sessions of <strong>
                    {formatMinutesToHours(course.courseExpertSupport.unitDurationMinutes)} </strong>each
                  </div>
                </div>
              </Radio>
            </Space>
          </Radio.Group>

          {course.courseExpertSupport.recommendationLevel === ExpertRecommendationLevelEnum.Required && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#ff4d4f' }}>
              Expert support is required for this course.
            </div>
          )}
        </>
      )}
    </div>
  )}
</Modal>
      {/* PRICE DETAIL MODAL */}
      <Modal
        title="Pricing & Access Information"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[<Button key="close" type="primary" onClick={() => setIsModalVisible(false)}>Got it</Button>]}
      >
        {selectedPriceData && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Price">
              <Text strong type="success" style={{ fontSize: '16px' }}>
                {selectedPriceData.pricing?.currency} {selectedPriceData.pricing?.price}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Access Duration">
              {formatDuration(selectedPriceData.pricing?.accessDurationDays)}
            </Descriptions.Item>

          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CourseDetail;